import { translateMessage, getLanguageFlag } from '@/utils/translateMessage';

export interface WebsiteAnalysisData {
  website_content: any[];
  website_info: any;
  key_content_pages: any[];
  post_ideas: any[];
  language: string;
  offering: string;
  goal: string;
}

export interface LanguageDetectionResult {
  language: string;
  websiteId: string;
  websiteUrl: string;
}

export const generateLanguageMessage = async (
  websiteLanguage: string,
  websiteId?: string,
  websiteUrl?: string
): Promise<string> => {
  try {
    // Gather website analysis data from localStorage
    const websiteAnalysisData: WebsiteAnalysisData = {
      website_content: JSON.parse(localStorage.getItem('website_content') || '[]'),
      website_info: JSON.parse(localStorage.getItem('website_info') || '{}'),
      key_content_pages: JSON.parse(localStorage.getItem('key_content_pages') || '[]'),
      post_ideas: JSON.parse(localStorage.getItem('post_ideas') || '[]'),
      language: websiteLanguage,
      offering: localStorage.getItem('website_offering') || '',
      goal: localStorage.getItem('website_goal') || ''
    };

    const translationResult = await translateMessage({
      message: `I can see the website is in a different language. Let me switch to your language so we can communicate better. What would you like to write about?`,
      targetLanguage: websiteLanguage,
      websiteId,
      websiteUrl,
      websiteAnalysisData
    });
    
    // Persist a local flag and the detected language
    localStorage.setItem('chat_language_switched', '1');
    localStorage.setItem('chat_language', websiteLanguage);
    
    const flag = getLanguageFlag(websiteLanguage);
    return `${flag} ${translationResult.translatedMessage}`;
  } catch (error) {
    console.error('Error generating language message:', error);
    // Fallback to English if AI generation fails
    localStorage.setItem('chat_language_switched', '1');
    localStorage.setItem('chat_language', websiteLanguage);
    return "I'm switching to your language now. What would you like to write about?";
  }
};

export const checkForLanguageSwitch = (): string | null => {
  const websiteLanguage = localStorage.getItem('website_language') || localStorage.getItem('language');
  const alreadySwitched = localStorage.getItem('chat_language_switched') === '1';
  
  if (websiteLanguage && websiteLanguage !== 'en' && !alreadySwitched) {
    return websiteLanguage;
  }
  
  return null;
};

export const handleLanguageDetectedEvent = async (
  event: CustomEvent<LanguageDetectionResult>,
  hasShownLanguageMessage: boolean
): Promise<string | null> => {
  const { language, websiteId, websiteUrl } = event.detail;
  const alreadySwitched = localStorage.getItem('chat_language_switched') === '1';
  
  if (language && language !== 'en' && !hasShownLanguageMessage && !alreadySwitched) {
    return await generateLanguageMessage(language, websiteId, websiteUrl);
  }
  
  return null;
};
